import axios from "axios";

const normalizeSurveyRows = (payload) => {
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.rows)) return payload.rows;
  if (Array.isArray(payload?.data)) return payload.data;
  return [];
};

const normalizeDemocracyCheckupTable = (year) => {
  const normalizedYear = String(year ?? "").trim();
  if (!/^\d{4}$/.test(normalizedYear)) return "";
  return `${normalizedYear}_democracy_checkup_clean`;
};

const surveyDataCache = new Map();
const surveyInFlightRequests = new Map();

const createAbortError = () => {
  const error = new Error("The request was aborted.");
  error.name = "AbortError";
  error.code = "ERR_CANCELED";
  return error;
};

const withAbortSignal = (promise, signal) => {
  if (!signal) return promise;
  if (signal.aborted) return Promise.reject(createAbortError());

  return new Promise((resolve, reject) => {
    const handleAbort = () => {
      signal.removeEventListener("abort", handleAbort);
      reject(createAbortError());
    };

    signal.addEventListener("abort", handleAbort, { once: true });
    promise
      .then((value) => {
        signal.removeEventListener("abort", handleAbort);
        resolve(value);
      })
      .catch((error) => {
        signal.removeEventListener("abort", handleAbort);
        reject(error);
      });
  });
};

const getCachedSurveyRows = async ({ cacheKey, requestFn, forceRefresh = false, signal }) => {
  if (forceRefresh) {
    surveyDataCache.delete(cacheKey);
    surveyInFlightRequests.delete(cacheKey);
  }

  if (surveyDataCache.has(cacheKey)) {
    return withAbortSignal(Promise.resolve(surveyDataCache.get(cacheKey)), signal);
  }

  if (!surveyInFlightRequests.has(cacheKey)) {
    const requestPromise = requestFn()
      .then((rows) => {
        surveyDataCache.set(cacheKey, rows);
        return rows;
      })
      .finally(() => {
        surveyInFlightRequests.delete(cacheKey);
      });

    surveyInFlightRequests.set(cacheKey, requestPromise);
  }

  return withAbortSignal(surveyInFlightRequests.get(cacheKey), signal);
};

export const isRequestAborted = (error) =>
  error?.name === "AbortError" || error?.code === "ERR_CANCELED";

export const fetchAllSurveyData = async (dbName, options = {}) => {
  const dbHeader = String(dbName || "").trim();
  if (!dbHeader) return [];

  try {
    return await getCachedSurveyRows({
      cacheKey: `db:${dbHeader}`,
      forceRefresh: Boolean(options.forceRefresh),
      signal: options.signal,
      requestFn: async () => {
        const response = await axios.get("/api/all_data", {
          headers: { db: dbHeader },
        });
        return normalizeSurveyRows(response?.data);
      },
    });
  } catch (error) {
    if (isRequestAborted(error)) throw error;

    const normalizedError = new Error("Failed to fetch survey data.");
    normalizedError.cause = error;
    throw normalizedError;
  }
};

export const fetchDemocracyCheckupData = async (year, options = {}) => {
  const table = normalizeDemocracyCheckupTable(year);
  if (!table) return [];

  try {
    return await getCachedSurveyRows({
      cacheKey: `dc:${table}`,
      forceRefresh: Boolean(options.forceRefresh),
      signal: options.signal,
      requestFn: async () => {
        const response = await axios.get("https://dataplayground.c-dem.ca/api/democracy_checkup", {
          params: { table },
        });

        return normalizeSurveyRows(response?.data);
      },
    });
  } catch (error) {
    if (isRequestAborted(error)) throw error;

    const normalizedError = new Error("Failed to fetch Democracy Checkup data.");
    normalizedError.cause = error;
    throw normalizedError;
  }
};
